import type React from 'react';

import { Box, Divider, FormField, FormHelp, Input, Label, Text, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../../../shared/models/index';
import { WATER_SEWER } from '../../../../../shared/constants';
import OptionSelector from './OptionSelector';
import HeaderToggle from './HeaderToggle';
import { isMultiFamily } from './MultiFamilyLandlordUtility';
import YesAndNo from '../../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function WaterSewer_v3({ section, setSectionValues }: Props): JSX.Element {
  const isMultiFamilyLandlord = isMultiFamily(section);
  const waterSewer_isEnabled = section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY);

  function setTenantResponsibility(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: '',
    });
  }

  function setLandlordResponsibility(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: null,
    });
  }

  function setServiceProvider(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function setBillAmount(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED),
    });
  }

  function yesDeliveredToAreas(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: true,
    });
  }

  function noDeliveredToAreas(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: false,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Water/sewer"
        subheader="Tenant pays in addition to rent"
        switchOn={waterSewer_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {waterSewer_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Box>
            <OptionSelector
              disabled={!waterSewer_isEnabled}
              optionsText="Who will be responsible for water/sewer set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content="Responsible for account set up and will bill tenant separately."
              onOption1Click={setTenantResponsibility}
              option1Selected={tenantSelected}
              onOption2Click={setLandlordResponsibility}
              option2Selected={landlordSelected}
            />
          </Box>

          {tenantSelected && (
            <>
              <FormField
                required
                label={<Label id="water-sewer-service-provider">What service provider?</Label>}
                control={
                  <Input
                    aria-labelledby="water-sewer-service-provider"
                    data-test="service-provider"
                    value={section.getFieldValue(WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER)}
                    onChange={setServiceProvider}
                  />
                }
                description={
                  <FormHelp>Hot water costs may be included in electricity or natural gas costs as applicable</FormHelp>
                }
              />
              {isMultiFamilyLandlord && <Box css={{ marginTop: 'layout.tight' }} />}
            </>
          )}

          {isMultiFamilyLandlord && tenantSelected && (
            <>
              <Text>Will this water/sewer service be delivered to areas other than the tenant’s unit?</Text>
              <YesAndNo
                yesActive={section.getFieldValue(
                  WATER_SEWER.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT,
                )}
                yesHandler={yesDeliveredToAreas}
                noHandler={noDeliveredToAreas}
              />
            </>
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <>
              <FormField
                required
                label={
                  <Label id="amount-to-bill-description">
                    How will you determine the amount to bill the tenant for water/sewer?
                  </Label>
                }
                control={
                  <Textarea
                    aria-labelledby="amount-to-bill-description"
                    css={{ resize: 'vertical' }}
                    onChange={setBillAmount}
                    value={section.getFieldValue(
                      WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT,
                    )}
                  />
                }
                description={
                  <FormHelp>
                    Example: The provider bills me per gallon used by the entire building. I distribute that cost evenly
                    among each of the building’s 5 units. Each unit pays 1/5 of the total cost.
                  </FormHelp>
                }
              />
            </>
          )}
        </>
      )}
    </>
  );
}
